// Constantes y tipos cerrados del panel de filtros de Obras (decisión Juan,
// panel 2026-07-09 — ver spec del preview aprobado). Archivo sin JSX: puede
// exportar tipos y valores libremente sin pisar la regla de Fast Refresh.
import type { BadgeProps } from '@/components/reui/badge';

// Nombres reales de las encargadas de Control RDT (Lista CC). El cruce
// contra usuarios reales de la app (para mostrar avatar/foto real, etc.)
// llega recién con la fase de edición — por ahora es un mapa fijo.
export const ENC_NOMBRES: Record<string, string> = {
  'A. Vera': 'Ana Vera',
  'A. Altamirando': 'Analía Altamirando',
  'M. Ferreira': 'Micaela Ferreira',
};

// Estados RDT tildados por default en el multiselect (los "en curso").
export const DEFAULT_RDT: string[] = ['Activo', 'Esperando Definitiva', 'A cargo de la UTE'];

// Orden fijo de las opciones del panel del multiselect de Estado RDT.
// "Archivado" queda deliberadamente afuera de este panel: se filtra vía el
// switch "Archivadas", no como opción tildable acá. "Central" tampoco figura
// (ajuste 2026-07-10): GET /api/obras excluye TOTAL las obras con estado RDT
// 'Central' (igual que con "Estado de obra", ver ESTADO_OBRA_ORDEN más abajo)
// — ninguna obra puede matchear esa opción, así que ofrecerla en el panel es
// un filtro señuelo (hallazgo del review adversarial).
export const RDT_OPCIONES_FIJAS: string[] = [...DEFAULT_RDT, 'No Aplica'];

// Pseudo-opción para las obras cuyo "Estado.Redes" viene VACÍO del maestro
// (7 de 258 al 2026-07-10, incluida una activa): sin ella serían invisibles
// en la home bajo cualquier combinación de filtros (hallazgo del review
// adversarial). Destildada por default para no alterar la vista default
// cerrada (las 66 obras de los 3 estados tildados). Solo aparece en el
// panel si hay al menos una obra en ese caso.
export const SIN_ESTADO_RDT = '(Sin estado)';

// Pseudo-opción del multiselect "Estado de obra" (ajuste home 2026-07-10)
// para las obras cuyo "Estado" (Lista CC) viene VACÍO — mismo criterio que
// SIN_ESTADO_RDT: solo aparece en el panel si hay al menos un caso, y TILDADA
// por default (acá el default es "todos tildados", a diferencia de RDT).
export const SIN_ESTADO_OBRA = '(Sin estado de obra)';

// Pseudo-opción del multiselect "Control RDT" para las obras sin "Control RDT"
// cargado en el maestro. Mismo criterio: solo aparece si hay al menos un
// caso, tildada por default. El valor es a la vez el sentinel del filtro
// (obras.tsx lo compara siempre por esta constante) y el texto visible de la
// opción (lenguaje neutro, BACKLOG 24/9/2026; la clave de los filtros
// persistidos subió a v3 para no arrastrar el valor viejo).
export const SIN_ENCARGADA = '(Sin Control RDT)';

// Orden del multiselect "Estado de obra"; los estados desconocidos que
// aparezcan en los datos se agregan al final, en orden alfabético (ver
// obras.tsx). 'Central' NO figura acá: esas obras quedan excluidas TOTAL en
// el backend (GET /api/obras las filtra por completo, decisión Juan
// 2026-07-10) — nunca llegan a la home, así que no tiene sentido ofrecerlas
// como opción de filtro.
export const ESTADO_OBRA_ORDEN: string[] = ['Activo', 'Pausada', 'Por Finalizar', 'Terminado', 'Terminado v2'];

// R0 (§2.h.2): mapas de variante para EstadoObraBadge / EstadoRedesBadge
// (estado-obra-badge.tsx), sobre reui/badge. Ajuste 15 (R-21/C-12): nunca
// una variante sólida de estado (`success|warning|info|destructive` a
// secas dan texto blanco sobre los pasteles de dark) — solo `-light` /
// `-outline`/`outline`/`secondary`. El mapa viejo de clases a mano
// (ESTADO_OBRA_BADGE) se borró al integrar R4a + T5 (rc.5): ya no quedaban
// consumidores.
export const VARIANTE_ESTADO_OBRA: Record<string, BadgeProps['variant']> = {
  Activo: 'success-light',
  Pausada: 'warning-light',
  'Por Finalizar': 'info-light',
  Terminado: 'secondary',
  'Terminado v2': 'secondary',
  Central: 'outline',
};
export const VARIANTE_ESTADO_OBRA_DEFAULT: BadgeProps['variant'] = 'secondary';

// «Esperando Definitiva» es el único estado de redes con acento propio
// (ámbar, ya lo tenía el chip a mano de estado-redes-dialog.tsx); el resto
// —incluido «Archivado»— es `outline` neutro (Archivado suma
// border-dashed + text-text-tertiary por className en el componente, no
// hay variante de Badge que lo exprese sola).
export const VARIANTE_ESTADO_REDES: Record<string, BadgeProps['variant']> = {
  'Esperando Definitiva': 'warning-outline',
};
export const VARIANTE_ESTADO_REDES_DEFAULT: BadgeProps['variant'] = 'outline';

// U-10 (rc.6, context/qa_visual_reui_2026-09-28.md): abreviatura visible del
// chip «RDT» (artboard `Home.dc.html` `rdtTxt`, distinto de `rdt` —
// hallazgo #4 vista / #2 reui-y-artboards, R4a). SOLO «Esperando Definitiva»
// es lo bastante largo para truncar en los layouts angostos (card de la
// home a 310px, chip del header de la obra). Vivía solo en `obra-card.tsx`
// — `header-obra.tsx` nunca la usaba y el chip del header se quedaba con el
// texto completo sin abreviar. `EstadoRedesBadge` sigue usando `estado`
// completo como clave de variante — esto es puramente el texto.
export const RDT_ETIQUETA_ABREVIADA: Partial<Record<string, string>> = {
  'Esperando Definitiva': 'Esp. Definitiva',
};
